import { FormattedMessage } from 'react-intl';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { FormSubGoal } from 'components/GoalsAndSubgoalsForm/interfaces';
import { useUndoToasts } from './UndoToasts.component';
import splitSubgoalWeights from './splitSubgoalWeights';
import { FormValues } from './interfaces';

export default function useSubgoalListActions(
  goalIndex: number,
  newSubgoal: () => Partial<FormSubGoal>,
) {
  const { control, setValue, getValues } = useFormContext<FormValues>();
  const { pushUndoToast } = useUndoToasts();
  const subgoalsPath = `goals.${goalIndex}.subgoals` as const;
  const { fields, append, remove, insert, move } = useFieldArray({
    control,
    name: subgoalsPath,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const markDirty = () => {
    setValue(subgoalsPath, getValues(subgoalsPath) as never, {
      shouldDirty: true,
    });
  };

  const handleAppendSubgoal = () => {
    append(newSubgoal() as never);
    if (getValues(`goals.${goalIndex}.id`)) {
      markDirty();
      return;
    }
    const subgoals = getValues(subgoalsPath) ?? [];
    const touchedSum = subgoals
      .filter((subgoal) => subgoal.weightTouched)
      .reduce((sum, subgoal) => sum + (Number(subgoal.weight) || 0), 0);
    const untouchedCount = subgoals.filter(
      (subgoal) => !subgoal.weightTouched,
    ).length;
    const weights = splitSubgoalWeights(100 - touchedSum, untouchedCount);
    let weightIdx = 0;
    const nextValue = subgoals.map((subgoal) => {
      if (subgoal.weightTouched) return subgoal;
      weightIdx += 1;
      return { ...subgoal, weight: weights[weightIdx - 1] };
    });
    setValue(subgoalsPath, nextValue as never, { shouldDirty: true });
  };

  const handleRemoveSubgoal = (idx: number) => {
    const removed = getValues(subgoalsPath)[idx];
    remove(idx);
    markDirty();
    pushUndoToast({
      message: (
        <FormattedMessage
          id="pages.scorecards.edit.subgoalDeleted"
          defaultMessage='Subgoal "{name}" deleted'
          values={{ name: removed?.name || `Subgoal ${idx + 1}` }}
        />
      ),
      onUndo: () => {
        insert(idx, removed as never);
        markDirty();
      },
    });
  };

  const handleReorderSubgoals = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIdx = fields.findIndex((f) => f.id === active.id);
    const toIdx = fields.findIndex((f) => f.id === over.id);
    if (fromIdx === -1 || toIdx === -1) return;
    move(fromIdx, toIdx);
    markDirty();
  };

  return {
    fields,
    sensors,
    handleAppendSubgoal,
    handleRemoveSubgoal,
    handleReorderSubgoals,
  };
}
